import React, { memo } from "react";
import {
  Box,
  HStack,
  Text,
  SimpleGrid,
  VStack,
  Divider,
  Tag,
} from "@chakra-ui/react";
import { HeaderWithStats } from "services/MetadataServices";
import AnalystCard from "./AnalystCard";

interface AnalystSectionProps {
  filteredHeaders: HeaderWithStats[];
  userId: string | undefined;
  defaultAnalystId: string;
  onAnalystSelection: (header: HeaderWithStats) => void;
  onRenameClick: (header: HeaderWithStats) => void;
  onDuplicateClick: (header: HeaderWithStats) => void;
  onDeleteClick: (header: HeaderWithStats) => void;
  onUpdateDefaultAnalyst: (header: HeaderWithStats) => void;
  isLoading?: boolean;
}

const AnalystSection: React.FC<AnalystSectionProps> = ({
  filteredHeaders,
  userId,
  defaultAnalystId,
  onAnalystSelection,
  onRenameClick,
  onDuplicateClick,
  onDeleteClick,
  onUpdateDefaultAnalyst,
  isLoading,
}) => {
  const yourAnalysts = filteredHeaders.filter(
    (h) => h.author_id === userId || h.id === defaultAnalystId
  );

  const othersAnalysts = filteredHeaders.filter(
    (h) => h.author_id !== userId && h.id !== defaultAnalystId
  );

  const cardProps = {
    defaultAnalystId,
    onAnalystSelection,
    onRenameClick,
    onDuplicateClick,
    onDeleteClick,
    onUpdateDefaultAnalyst,
    isLoading,
  };

  const renderSection = (title: string, headers: HeaderWithStats[]) => {
    if (headers.length === 0) return null;
    return (
      <Box>
        <HStack spacing={3} mb={4}>
          <Text fontSize="md" fontWeight="700" color="gray.700" mb={0}>
            {title}
          </Text>
          <Tag
            size="sm"
            colorScheme="purple"
            borderRadius="full"
            fontWeight="600"
            bg="purple.600"
            color="white"
          >
            {headers.length}
          </Tag>
          <Divider flex="1" borderColor="gray.200" />
        </HStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 3 }} spacing={6}>
          {headers.map((header) => (
            <AnalystCard key={header.id} header={header} {...cardProps} />
          ))}
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <VStack spacing={8} align="stretch">
      {renderSection("Your Analysts", yourAnalysts)}
      {renderSection("Others' Analysts", othersAnalysts)}
    </VStack>
  );
};

export default memo(AnalystSection); // Memoize to prevent unnecessary re-renders of the component.
